import * as React from "react"
import { Check, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

export interface ConfirmTemplateAction {
  label: string
  value?: string
}

export interface ConfirmTemplateProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  message: string
  confirmAction?: ConfirmTemplateAction
  cancelAction?: ConfirmTemplateAction
  onConfirm: (value?: string) => void
  onCancel: (value?: string) => void
  showIcons?: boolean
  variant?: "default" | "destructive"
}

const ConfirmTemplateInner = React.forwardRef<HTMLDivElement, ConfirmTemplateProps>(
  (
    {
      className,
      title,
      message,
      confirmAction = { label: "Yes" },
      cancelAction = { label: "No" },
      onConfirm,
      onCancel,
      showIcons = true,
      variant = "default",
      ...props
    },
    ref
  ) => {
    const handleConfirm = React.useCallback(() => {
      onConfirm(confirmAction.value)
    }, [onConfirm, confirmAction.value])

    const handleCancel = React.useCallback(() => {
      onCancel(cancelAction.value)
    }, [onCancel, cancelAction.value])

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent, action: "confirm" | "cancel") => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          action === "confirm" ? handleConfirm() : handleCancel()
        }
      },
      [handleConfirm, handleCancel]
    )

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-lg border border-border bg-card p-4 shadow-sm",
          className
        )}
        role="alertdialog"
        aria-labelledby={title ? "confirm-title" : undefined}
        aria-describedby="confirm-message"
        {...props}
      >
        <div className="space-y-4">
          {/* Message Section */}
          <div className="space-y-2">
            {title && (
              <h3
                id="confirm-title"
                className="font-semibold text-base text-foreground"
              >
                {title}
              </h3>
            )}
            <p
              id="confirm-message"
              className="text-sm text-muted-foreground leading-relaxed"
            >
              {message}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            {/* Cancel/No Button */}
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCancel}
              onKeyDown={(e) => handleKeyDown(e, "cancel")}
              aria-label={cancelAction.label}
            >
              {showIcons && <X className="size-4 mr-2" />}
              {cancelAction.label}
            </Button>

            {/* Confirm/Yes Button */}
            <Button
              variant={variant === "destructive" ? "destructive" : "default"}
              className="flex-1"
              onClick={handleConfirm}
              onKeyDown={(e) => handleKeyDown(e, "confirm")}
              aria-label={confirmAction.label}
              autoFocus
            >
              {showIcons && <Check className="size-4 mr-2" />}
              {confirmAction.label}
            </Button>
          </div>
        </div>
      </div>
    )
  }
)

ConfirmTemplateInner.displayName = "ConfirmTemplateInner"

// Memoize to prevent re-renders when parent re-renders
const ConfirmTemplate = React.memo(ConfirmTemplateInner)
ConfirmTemplate.displayName = "ConfirmTemplate"

export { ConfirmTemplate }
